/** A geocode as returned by the optimizer backend (note: `long`, not `lng`). */
export interface Geocodes { lat: number; long: number }

/** One stop on a route (backend ORDER_RECORD). HUB stops carry `address`. */
export interface Stop {
  id: string                       // order doc id, or "START***Hub" / "END***Hub"
  invoice: string
  vehicle: string
  client: string                   // "BusinessUnit***BusinessSubUnit"
  customer?: string                // customer/recipient name (shown in StopInfo popup)
  orderType: string                // "Delivery" | "Pickup" | "HUB"
  orderTypeShort: string           // "D" | "P" | "H"
  pickupDelivery?: string
  driver: string
  geocodes: Geocodes
  linkedOrder?: string | null
  linkedRef?: string | null
  startWindow: string              // ISO 8601
  endWindow: string                // ISO 8601
  lockedStartWindow?: string
  lockedEndWindow?: string
  serviceTime: number              // minutes (backend getTotalServiceTime returns minutes)
  productJson?: any[]              // optional product line items (Qty/Product) for the popup
  vehicleLocked?: boolean
  timeLocked?: boolean
  optimized?: boolean
  productCount?: number
  totalWeight?: number             // sum of product Weight×Qty (from getRoutes)
  totalVolume?: number             // sum of product Volume×Qty (from getRoutes)
  quantityType?: string
  distributionZone?: string
  serviceType?: string
  vehicleType?: string
  vehicleTypeSkill?: string | null
  address?: string                 // HUB only
  orderStatus?: string
}

export interface RouteDriver { id: string; firstName: string; lastName: string; email: string; name: string }

/** A route = one vehicle (or pseudo-vehicle) and its ordered stops (backend ROUTE_NEW). */
export interface Route {
  vehicle: string                  // "Truck1" | "PreRoute" | "ReadyToRoute" | "Unfit Stops"
  driver: RouteDriver | null
  stops: Stop[]
  visible: boolean
  isTruckLocked: boolean
  deliveryAssignee: string | null
  optimized: boolean
  showRoute: boolean
  shouldCheckRoute: boolean
  totalDistance?: number           // kilometres (backend optimize/calculate output)
  totalTime?: number | string      // preformatted string e.g. "12h 46m" (backend); number = legacy seconds
}

export interface VehicleLocation { name: string; geocodes: Geocodes }

/** Fleet vehicle (backend VEHICLE_RECORD). */
export interface Vehicle {
  id: string
  name: string
  vehicleType: string
  vehicleTypeSkill?: string | null
  capacity: number
  volumeCapacity: number
  weightCapacity: number
  region: string
  isActive: boolean
  startLocation: VehicleLocation
  endLocation: VehicleLocation
  shiftStart: string               // ISO 8601
  shiftEnd: string                 // ISO 8601
  returnToStart: boolean
  driver?: RouteDriver | null
  driverName?: string
}

export interface DriverOption {
  title: string
  desc: string
  value: string                    // "Name***username"
  // firstName/lastName/email carry through to the assigned driver object so the
  // publish modal can show the name + gate push-to-driver (which needs email).
  // `id` and `firestoreDocId` are both the USERS doc id — what getRoutes/getFleets
  // report as driver.id, and the only id a USERS write can key on. (The raw
  // driverAppUserName lives in `value` as "Name***username".)
  data: { id: string; active: boolean; distributionZones: string[]; firstName?: string; lastName?: string; email?: string; firestoreDocId?: string }
}

export interface DistributionZone { id: string; name: string; region: string }

/** Full getRoutes / getFleets response. */
export interface RoutesResponse {
  drivers: DriverOption[]
  routes: Route[]
  vehicles: Vehicle[]
  selectedClients: string[]
  distributionZones: DistributionZone[]
  missingVehicles: string[]
}

export type JobStatus = 'idle' | 'submitting' | 'inProgress' | 'success' | 'error'

/** getJspritSolution response. */
export interface JobSolution { routes: Route[]; status: 'inProgress' | 'success' | 'error'; errorMessage?: string }

export const PSEUDO_ROUTES = ['PreRoute', 'ReadyToRoute', 'Unfit Stops'] as const
export type PseudoRoute = typeof PSEUDO_ROUTES[number]
export const isPseudoRoute = (vehicle: string): vehicle is PseudoRoute =>
  (PSEUDO_ROUTES as readonly string[]).includes(vehicle)

/** Human-friendly column label. Only the run-together pseudo names get spaced;
 *  PreRoute and real vehicle names pass through unchanged. */
const ROUTE_DISPLAY_NAMES: Record<string, string> = { ReadyToRoute: 'Ready To Route' }
export const routeDisplayName = (vehicle: string): string => ROUTE_DISPLAY_NAMES[vehicle] ?? vehicle
